import { useEffect, useState, useSyncExternalStore, type FC } from "react";
import { Modal, Alert, Empty } from "antd";
import LoadingSkeleton from "../../components/CustomComponents/LoadingSkeleton";
import {
  getVoiceCallAudio,
  getVoiceCallTranscript,
  type VoiceCallTranscriptResponse,
} from "./neoApi";
import { getTheWordClinic, useNeoClinicWord } from "./neoClinicWord";

/**
 * Popup that shows a Neo voice call's transcript (scrollable) and playable
 * audio. Opened from the `get_voice_call` tool chip.
 *
 * A tiny module-level external store lets the deeply-nested tool chip open the
 * modal without threading callbacks through the 9k-line Neo page. Mount
 * <VoiceCallTranscriptModalHost /> once at the page root; call
 * openVoiceCallModal(conversationId) from anywhere.
 */

let currentConversationId: string | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

export const voiceCallModalStore = {
  open(conversationId: string) {
    currentConversationId = conversationId;
    emit();
  },
  close() {
    currentConversationId = null;
    emit();
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot() {
    return currentConversationId;
  },
};

export const openVoiceCallModal = (conversationId: string) =>
  voiceCallModalStore.open(conversationId);

const roleLabel = (role?: string) => {
  if (role === "agent") return "Assistant";
  if (role === "user") return "Caller";
  return role || "—";
};

const formatDuration = (secs?: number | null): string | null => {
  if (typeof secs !== "number" || secs < 0) return null;
  const total = Math.floor(secs);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const VoiceCallTranscriptModalInner: FC<{
  conversationId: string;
  onClose: () => void;
}> = ({ conversationId, onClose }) => {
  // Re-render this modal's copy when the care setting resolves.
  useNeoClinicWord();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] =
    useState<VoiceCallTranscriptResponse | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTranscript(null);
    setAudioUrl(null);

    (async () => {
      try {
        const t = await getVoiceCallTranscript(conversationId);
        if (cancelled) return;
        setTranscript(t);

        if (t.ready && t.audio_available) {
          try {
            const a = await getVoiceCallAudio(conversationId);
            if (!cancelled && a.ready && a.url) setAudioUrl(a.url);
          } catch {
            // Audio is best-effort; the transcript is the primary content.
          }
        }
      } catch (e) {
        if (cancelled) return;
        const status = (e as { response?: { status?: number } })?.response
          ?.status;
        if (status === 403) {
          setError(
            `You do not have access to the ${getTheWordClinic()} where this call took place.`
          );
        } else if (status === 404) {
          setError("No record of this call was found.");
        } else {
          setError("Could not load this call. Please try again shortly.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const duration = formatDuration(transcript?.duration_secs);

  return (
    <Modal
      open
      title="Voice call"
      onCancel={onClose}
      footer={null}
      width={680}
      destroyOnClose
    >
      {loading && <LoadingSkeleton ariaLabel="Loading transcript" rows={5} />}

      {!loading && error && <Alert type="error" showIcon message={error} />}

      {!loading && !error && transcript && !transcript.ready && (
        <Alert
          type="info"
          showIcon
          message="This call's transcript isn't available yet."
          description="The call may still be in progress or finishing processing. Try again in a little while."
        />
      )}

      {!loading && !error && transcript && transcript.ready && (
        <div>
          {transcript.transcript_summary && (
            <p style={{ marginTop: 0 }}>{transcript.transcript_summary}</p>
          )}

          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              color: "rgba(0,0,0,0.55)",
              fontSize: "var(--neo-design-32-font-size, 12px)",
              marginBottom: 12,
            }}
          >
            {transcript.status && <span>Status: {transcript.status}</span>}
            {duration && <span>Duration: {duration}</span>}
            {transcript.call_successful && (
              <span>Outcome: {transcript.call_successful}</span>
            )}
            {typeof transcript.message_count === "number" && (
              <span>{transcript.message_count} turns</span>
            )}
          </div>

          {audioUrl && (
            <audio
              controls
              src={audioUrl}
              style={{ width: "100%", marginBottom: 16 }}
            >
              Your browser does not support audio playback.
            </audio>
          )}

          <div
            style={{
              maxHeight: 420,
              overflowY: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: 12,
            }}
          >
            {transcript.transcript && transcript.transcript.length > 0 ? (
              transcript.transcript.map((turn, idx) => {
                const isAgent = turn.role === "agent";
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isAgent ? "flex-start" : "flex-end",
                      marginBottom: 10,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "var(--neo-design-32-font-size, 11px)",
                        color: "rgba(0,0,0,0.45)",
                        marginBottom: 2,
                      }}
                    >
                      {roleLabel(turn.role)}
                    </span>
                    <div
                      style={{
                        maxWidth: "85%",
                        padding: "8px 12px",
                        borderRadius: 12,
                        background: isAgent ? "#f5f5f5" : "#e6f4ff",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {turn.message}
                    </div>
                  </div>
                );
              })
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No transcript turns"
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

/** Mount once at the Neo page root. Renders nothing until a call is opened. */
export const VoiceCallTranscriptModalHost: FC = () => {
  const conversationId = useSyncExternalStore(
    voiceCallModalStore.subscribe,
    voiceCallModalStore.getSnapshot
  );

  useEffect(() => () => voiceCallModalStore.close(), []);

  if (!conversationId) return null;
  return (
    <VoiceCallTranscriptModalInner
      conversationId={conversationId}
      onClose={() => voiceCallModalStore.close()}
    />
  );
};
